import {
  isLatitudeType,
  isLongitudeType,
  isPlottableCanonicalType,
  type MessagePathNode,
  CanonicalDataType,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";
import { type PanelState, PanelType } from "@/shared/state/visualization";

import { isPointCloudDataPath } from "../PanelSettings/ThreeDPanelSettings/EntityConfig/isPointCloudDataPath";

export function isViewableAsImage(messagePath: MessagePathNode) {
  return messagePath.data.canonical_data_type === CanonicalDataType.Image;
}

export function isViewableAsPlot(messagePath: MessagePathNode) {
  // The type set is shared with the record-level check panel creation runs, so
  // the two cannot drift; see `isPlottableCanonicalType`. The PlotPanel picks
  // values out of arrays and coerces them to numbers, raising if it cannot.
  return (
    messagePath.isLeafNode() &&
    isPlottableCanonicalType(messagePath.data.canonical_data_type)
  );
}

/**
 * What the 3D panel can show from the topic tree: a point cloud's `data`. A
 * position or a rotation is bound in the entity settings, from several paths
 * at once, so a single path has no place to drop into.
 */
export function isViewableAsThreeD(messagePath: MessagePathNode) {
  return isPointCloudDataPath(messagePath);
}

export function isViewableAsMap(messagePath: MessagePathNode) {
  // Note(ENG-2040): Prevent numerical types to be viewed as map
  // until we have a good UX to assign paths to lat/lon slots.
  if (messagePath.data.canonical_data_type === CanonicalDataType.Number) {
    return false;
  }

  return (
    isLatitudeType(messagePath.data.canonical_data_type) ||
    isLongitudeType(messagePath.data.canonical_data_type)
  );
}

export function isViewableAsRawMessage(messagePath: MessagePathNode): boolean {
  return (
    messagePath.hasRepresentation(RepresentationStorageFormat.MCAP) ||
    messagePath.hasRepresentation(RepresentationStorageFormat.PARQUET)
  );
}

export function isViewableAsConsoleLog(messagePath: MessagePathNode) {
  // A log column renders via the MCAP/Parquet readers, so it needs a readable
  // representation; a path with none can't be shown, and toTopicData() would
  // throw if such a path reached the add path.
  return (
    !isViewableAsImage(messagePath) &&
    (messagePath.hasRepresentation(RepresentationStorageFormat.MCAP) ||
      messagePath.hasRepresentation(RepresentationStorageFormat.PARQUET))
  );
}

export function panelAcceptsMessagePath(
  panel: PanelState,
  messagePath: MessagePathNode,
): boolean {
  const panelType = panel.type;
  switch (panelType) {
    case PanelType.Plot:
      return isViewableAsPlot(messagePath);
    case PanelType.ThreeD:
      return isViewableAsThreeD(messagePath);
    case PanelType.RawMessage:
      return isViewableAsRawMessage(messagePath);
    case PanelType.Image:
      return isViewableAsImage(messagePath);
    case PanelType.Map:
      return isViewableAsMap(messagePath);
    case PanelType.Log:
      return isViewableAsConsoleLog(messagePath);
    default:
      return false;
  }
}
